// BookAMeeting.jsx
// Main container for the "Schedule A Meet" popup.
//
// Exports:
//   - <BookAMeetingModal open onClose />  : the popup itself (controlled)
//   - <BookAMeetingButton />              : the header pill button that opens it
//
// Role gating: the "Schedule" tab is student-only. Teachers see only
// "Upcoming Meeting" and "Meeting History". Role is read from redux globalState.

import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { updateGlobalState } from "../../state/global/slice"; // <-- adjust path
import Integrations from "./Integrations";
import ScheduleMeeting from "./ScheduleMeeting";
import UpcomingMeeting from "./UpcomingMeeting";
import MeetingHistory from "./MeetingHistory";
import ScheduleSuccess from "./ScheduleSuccess";
import "./bookAMeeting.css";
import {google_calendar} from "../../assets/images/images"

const BookAMeetingModal = ({ open, onClose, onScheduled }) => {
  const globalState = useSelector((state) => state.globalState);
  const role = globalState?.role;
  const isStudent = role === "Student";

  // Bumped to force the upcoming/history tabs to refetch after a change.
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey((k) => k + 1);

  // Count shown next to the "Upcoming Meeting" tab label.
  const [upcomingCount, setUpcomingCount] = useState(0);

  // Google Calendar connection state (shared with the Integrations tab), read
  // straight from globalState. Reflects the backend's has_google_meet_credential.
  const connFlag =
    globalState?.student_details?.data?.has_google_meet_credential ||
    globalState?.teacher_details?.data?.has_google_meet_credential ||
    false;
  const acctName =
    globalState?.student_details?.data?.google_account ??
    globalState?.teacher_details?.data?.google_account ??
    "";
  const [googleConnected, setGoogleConnected] = useState(!!connFlag);
  const [googleAccount, setGoogleAccount] = useState(acctName);
  useEffect(() => {
    setGoogleConnected(!!connFlag);
  }, [connFlag]);

  // Teacher "Enable Calendar Meetings" — the single source of truth is
  // globalState (is_available_for_meet), so the modal toggle and the Settings
  // page toggle stay in sync. Writing goes through updateGlobalState.
  const dispatch = useDispatch();
  const teacherDetails = globalState?.teacher_details;
  const teacherAvailable = !!teacherDetails?.data?.is_available_for_meet;
  const setTeacherAvailable = (next) => {
    if (!teacherDetails) return;
    dispatch(
      updateGlobalState({
        key: "teacher_details",
        value: {
          ...teacherDetails,
          data: { ...teacherDetails.data, is_available_for_meet: next },
        },
      })
    );
  };

  // Tabs: Integrations first (both roles), then Schedule (students only),
  // then Upcoming + History.
  const tabs = useMemo(() => {
    const upcomingLabel = isStudent ? "Upcoming Meetings" : "Scheduled Meetings";
    const base = [
      { key: "upcoming", label: upcomingLabel },
      { key: "history", label: "Meeting History" },
    ];
    const withSchedule = isStudent
      ? [{ key: "schedule", label: "Schedule" }, ...base]
      : base;
    return [{ key: "settings", label: "Settings" }, ...withSchedule];
  }, [isStudent]);

  const [activeTab, setActiveTab] = useState(tabs[0].key);

  // Keep the active tab valid if the role/tab set changes.
  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) setActiveTab(tabs[0].key);
  }, [tabs, activeTab]);

  // Default tab on open: Integrations if Google isn't connected yet (connect
  // first); otherwise straight to Schedule (students) / Upcoming (teachers).
  useEffect(() => {
    if (!open) return;
    const def = !googleConnected
      ? "settings"
      : isStudent
      ? "schedule"
      : "upcoming";
    setActiveTab(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Until Google Calendar is connected, the Schedule + Upcoming *content* is
  // shown but faded and non-interactive. The tabs themselves stay clickable, and
  // Integrations + Meeting History work normally.
  const contentLocked =
    !googleConnected && (activeTab === "schedule" || activeTab === "upcoming");

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const tabLabel = (t) =>
    t.key === "upcoming" && upcomingCount > 0
      ? `${t.label} (${String(upcomingCount).padStart(2, "0")})`
      : t.label;

  return (
    <div className="bm-overlay" onMouseDown={onClose}>
      <div
        className="bm-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Meeting"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bm-modal-head">
          <h3 className="bm-modal-title">
            {isStudent ? "Schedule A Meet" : "My Meeting"}
          </h3>
          <button
            type="button"
            className="bm-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="bm-tabs">
          {tabs.map((t) => (
            <button
              type="button"
              key={t.key}
              className={`bm-tab ${activeTab === t.key ? "bm-tab-active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >
              {tabLabel(t)}
            </button>
          ))}
        </div>

        <div className={`bm-tab-body ${contentLocked ? "bm-locked" : ""}`}>
          {activeTab === "settings" && (
            <Integrations
              role={role}
              connected={googleConnected}
              account={googleAccount}
              available={teacherAvailable}
              onAvailabilityChange={setTeacherAvailable}
              onConnected={(accountName) => {
                setGoogleConnected(true);
                if (accountName) setGoogleAccount(accountName);
              }}
              onDisconnected={() => {
                setGoogleConnected(false);
                setGoogleAccount("");
              }}
            />
          )}

          {activeTab === "schedule" && isStudent && (
            <ScheduleMeeting
              onBooked={(meeting) => {
                // hand the created meeting up so the parent can show the
                // success popup (and close this modal)
                onScheduled?.(meeting);
              }}
            />
          )}

          {activeTab === "upcoming" && (
            <UpcomingMeeting
              refreshKey={refreshKey}
              onChanged={bumpRefresh}
              onCountChange={setUpcomingCount}
            />
          )}

          {activeTab === "history" && <MeetingHistory refreshKey={refreshKey} />}
        </div>
      </div>
    </div>
  );
};

// Convenience wrapper: the header button that navigates to the meetings page.
// Drop this straight into the Dashboard header for both roles — the label switches by role
// ("Book a meeting" for students, "My meetings" for teachers).
export const BookAMeetingButton = ({ className = "" }) => {
  const role = useSelector((state) => state.globalState?.role);
  const label = "My Meetings";
  const navigate = useNavigate();

  return (
      <button
        type="button"
        className={`bm-trigger-btn ${className} bg-[#FAFAFA]`}
        onClick={() => navigate("/dashboard/meetings")}
      >
        <img src={google_calendar} alt="google_calendar" style={{width:"20px"}}/>
        {label}
      </button>
  );
};

export default BookAMeetingModal;